import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import {
  Home,
  Blocks,
  ArrowRightLeft,
  Users,
  Coins,
  Shield,
  BarChart3,
  ChevronDown,
  Code,
  Zap,
  Activity,
  Database,
  FileText,
  Search,
  Map,
  Menu,
  X,
  Wallet,
  DollarSign,
  TrendingUp,
  Gem,
  Settings,
  ArrowUpDown,
  Award,
  Flame,
  Target,
  Repeat
} from 'lucide-react'
import { clsx } from 'clsx'
import { ThemeSwitcher } from '../common/ThemeSwitcher'
import { log } from '../../utils/logger'
import centralizedValidatorService from '@/services/centralizedValidatorService'

interface NavigationItem {
  name: string
  href: string
  icon: React.ComponentType<{ className?: string }>
  description?: string
  badge?: string
  children?: NavigationItem[]
}

const navigation: NavigationItem[] = [
  // CORE PAGES - Direct access to main features
  {
    name: 'Dashboard',
    href: '/',
    icon: Home,
    description: 'Network overview and statistics'
  },
  {
    name: 'Blocks',
    href: '/blocks',
    icon: Blocks,
    description: 'Recent blocks and block details'
  },
  {
    name: 'Transactions',
    href: '/transactions',
    icon: ArrowRightLeft,
    description: 'Transaction history and details'
  },

  // ASSETS - Tokens and NFTs
  {
    name: 'Assets',
    href: '#',
    icon: Gem,
    description: 'Tokens and NFTs on the network',
    children: [
      { name: 'Tokens', href: '/tokens', icon: Coins, description: 'Token analytics and market data' },
      { name: 'NFTs', href: '/nfts', icon: FileText, description: 'Non-fungible token collections' },
    ]
  },

  // VALIDATORS - Grouped validator features
  {
    name: 'Validators',
    href: '#',
    icon: Shield,
    description: 'Network validators and staking',
    children: [
      { name: 'Validator List', href: '/validators', icon: Shield, description: 'Browse all network validators' },
      { name: 'Validator Map', href: '/validator-map', icon: Map, description: 'Geographic validator distribution' },
      { name: 'Staking', href: '/staking', icon: TrendingUp, description: 'Staking rewards and statistics' },
      { name: 'Program Delegations', href: '/delegations', icon: Award, description: 'X1 Delegation Program stake distribution' },
    ]
  },

  // ANALYTICS
  {
    name: 'Analytics',
    href: '#',
    icon: BarChart3,
    description: 'Network analytics and insights',
    children: [
      { name: 'Address Statistics', href: '/analytics/address-statistics', icon: Users, description: 'Address analytics and activity' },
      { name: 'Bridge', href: '/analytics/bridge', icon: ArrowRightLeft, description: 'Warp Bridge command center — flows, latency, collateral' },
      { name: 'Fee Tracker', href: '/analytics/fee-tracker', icon: DollarSign, description: 'Transaction fee monitoring and burn analytics' },
      { name: 'Gas Tracker', href: '/gas-tracker', icon: Zap, description: 'Gas price monitoring and trends' },
      { name: 'Network Stats', href: '/network-stats', icon: Activity, description: 'Overall network metrics and statistics' },
      { name: 'Programs', href: '/programs', icon: Code, description: 'Smart contract programs on the network' },
      { name: 'XNT Token', href: '/analytics/xnt', icon: Coins, description: 'XNT token supply, staking, and tokenomics' },
    ]
  },

  // PORTFOLIO - single direct link; sub-pages are reached via the in-suite nav rail
  {
    name: 'Portfolio',
    href: '/portfolio',
    icon: Wallet,
    description: 'View and manage your portfolio',
  },
]

interface TopNavigationProps {
  mobileMenuOpen: boolean
  setMobileMenuOpen: (open: boolean) => void
}

export const TopNavigation: React.FC<TopNavigationProps> = ({ mobileMenuOpen, setMobileMenuOpen }) => {
  const location = useLocation()
  const queryClient = useQueryClient()
  const [openDropdown, setOpenDropdown] = React.useState<string | null>(null)
  const [hoverModeActive, setHoverModeActive] = React.useState(false)
  const [discoveredPrograms, setDiscoveredPrograms] = React.useState<any[]>([])
  const closeTimeoutRef = React.useRef<NodeJS.Timeout | null>(null)

  /**
   * Prefetch data for a route to enable instant navigation
   * This runs on mouse hover over navigation links
   */
  const prefetchRouteData = React.useCallback((href: string) => {
    // Prefetch validators data for validator-related routes
    if (href.includes('/validators') || href.includes('/validator-map') || href.includes('/staking')) {
      queryClient.prefetchQuery({
        queryKey: ['validators'],
        queryFn: () => centralizedValidatorService.getAllValidators(),
        staleTime: 3 * 60 * 1000, // 3 minutes
      })
    }

    // Add more route-specific prefetching as needed
    // This can be expanded to prefetch blocks, transactions, etc.
  }, [queryClient])

  const isActive = (href: string) => {
    if (href === '/') {
      return location.pathname === '/'
    }
    return location.pathname.startsWith(href)
  }

  const isParentActive = (item: NavigationItem) => {
    if (isActive(item.href)) return true
    if (item.children) {
      return item.children.some(child => isActive(child.href))
    }
    return false
  }

  const handleDropdownToggle = (name: string) => {
    const newState = openDropdown === name ? null : name
    setOpenDropdown(newState)
    // Activate hover mode when any dropdown is opened via click
    setHoverModeActive(newState !== null)
  }

  const handleMouseEnter = (name: string) => {
    // Clear any pending close timeout
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }

    // Only respond to hover if hover mode is active (after initial click)
    if (hoverModeActive) {
      // Instant dropdown switching - no delay
      setOpenDropdown(name)
    }
  }

  const handleMouseLeave = (event: React.MouseEvent) => {
    // Only auto-close if hover mode is active
    if (hoverModeActive) {
      // Clear any existing timeout first
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current)
        closeTimeoutRef.current = null
      }

      // Check if mouse is moving to another dropdown trigger (adjacent menu)
      const relatedTarget = event.relatedTarget as HTMLElement
      const isMovingToDropdown = relatedTarget?.closest('[data-dropdown-trigger]') ||
                                relatedTarget?.closest('[data-dropdown-menu]')

      if (isMovingToDropdown) {
        // Don't close if moving to another dropdown - let the new one handle the switch
        return
      }

      // Longer delay to give user time to move between adjacent dropdowns
      closeTimeoutRef.current = setTimeout(() => {
        setOpenDropdown(null)
        setHoverModeActive(false)
        closeTimeoutRef.current = null
      }, 500) // Increased from 300ms to 500ms
    }
  }

  React.useEffect(() => {
    const handleClickOutside = (event: Event) => {
      // Only close dropdown if clicking outside the navigation
      const target = event.target as HTMLElement
      if (!target.closest('nav')) {
        // Clear any pending close timeout
        if (closeTimeoutRef.current) {
          clearTimeout(closeTimeoutRef.current)
          closeTimeoutRef.current = null
        }
        setOpenDropdown(null)
        setHoverModeActive(false)
      }
    }

    const handleXenDEXProgramsDiscovered = (event: CustomEvent) => {
      log.debug('Navigation: Received discovered XenDEX programs', event.detail.programs)

      // Only include programs with real verified activity
      const verifiedPrograms = event.detail.programs.filter((program: any) => {
        return (
          program.confidence > 80 && // High confidence only
          program.accountCount > 10 && // Must have real activity
          program.type === 'discovered' && // Only discovered programs
          program.evidence && program.evidence.length > 0 // Must have evidence
        )
      })

      log.debug(`Navigation: Filtered to ${verifiedPrograms.length} verified programs with real activity`)
      setDiscoveredPrograms(verifiedPrograms)
    }

    // Load previously discovered programs from localStorage
    const loadStoredPrograms = () => {
      try {
        const stored = localStorage.getItem('xenDEXDiscoveredPrograms')
        if (stored) {
          const programs = JSON.parse(stored)

          // Apply same filtering to stored programs
          const verifiedPrograms = programs.filter((program: any) => {
            return (
              program.confidence > 80 && // High confidence only
              program.accountCount > 10 && // Must have real activity
              program.type === 'discovered' && // Only discovered programs
              program.evidence && program.evidence.length > 0 // Must have evidence
            )
          })

          setDiscoveredPrograms(verifiedPrograms)
          log.debug(`Navigation: Loaded ${verifiedPrograms.length} verified programs (filtered from ${programs.length} stored)`)
        }
      } catch (error) {
        log.error('Error loading stored programs:', error)
      }
    }

    document.addEventListener('click', handleClickOutside)
    window.addEventListener('xenDEXProgramsDiscovered', handleXenDEXProgramsDiscovered as EventListener)
    loadStoredPrograms()

    return () => {
      document.removeEventListener('click', handleClickOutside)
      window.removeEventListener('xenDEXProgramsDiscovered', handleXenDEXProgramsDiscovered as EventListener)
      // Cleanup any pending timeout on unmount
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current)
        closeTimeoutRef.current = null
      }
    }
  }, [])

  // Generate dynamic navigation with discovered programs
  const getDynamicNavigation = (): NavigationItem[] => {
    const baseNavigation = [...navigation]

    // Find the Analytics section
    const analyticsIndex = baseNavigation.findIndex(item => item.name === 'Analytics')

    if (analyticsIndex !== -1 && discoveredPrograms.length > 0) {
      // Add discovered programs to the DeFi Analytics section
      const analyticsItem = baseNavigation[analyticsIndex]

      const dynamicXenDEXPrograms = discoveredPrograms.map(program => ({
        name: program.name,
        href: `/defi/${program.id.toLowerCase()}`,
        icon: getIconForProgram(program.category),
        description: `${program.description} (${program.accountCount} accounts)`
      }))

      // Update the Analytics children to include discovered programs
      if (analyticsItem.children) {
        const existingXenDEXIndex = analyticsItem.children.findIndex(child => child.name === 'XDex')

        if (existingXenDEXIndex !== -1) {
          // Replace the generic XDex entry with specific discovered programs
          const updatedChildren = [
            ...analyticsItem.children.slice(0, existingXenDEXIndex + 1),
            ...dynamicXenDEXPrograms,
            ...analyticsItem.children.slice(existingXenDEXIndex + 1)
          ]

          analyticsItem.children = updatedChildren
        }
      }
    }

    return baseNavigation
  }

  const getIconForProgram = (category: string) => {
    // Only handle categories we actually find through blockchain analysis
    if (category.includes('AMM') || category.includes('Market') || category.includes('DEX')) return ArrowUpDown
    if (category.includes('Staking') || category.includes('Stake')) return TrendingUp
    if (category.includes('Farm') || category.includes('Farming')) return Gem
    return Activity // Default icon for unknown discovered programs
  }

  const dynamicNavigation = getDynamicNavigation()

  return (
    <nav className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
      <div className="px-4 sm:px-6 lg:px-8">
        {/* Desktop Navigation */}
        <div className="hidden lg:flex justify-end space-x-8">
          {dynamicNavigation.map((item) => (
            <div key={item.name} className="relative">
              {item.children ? (
                <div
                  className="relative"
                  data-dropdown-trigger
                  onMouseEnter={() => handleMouseEnter(item.name)}
                  onMouseLeave={handleMouseLeave}
                >
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      handleDropdownToggle(item.name)
                    }}
                    className={clsx(
                      'flex items-center px-3 py-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap cursor-pointer',
                      isParentActive(item)
                        ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                    )}
                  >
                    <item.icon className="mr-2 h-4 w-4 flex-shrink-0" />
                    {item.name}
                    {item.badge && (
                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300">
                        {item.badge}
                      </span>
                    )}
                    <ChevronDown className={clsx(
                      'ml-1 h-4 w-4 transition-transform',
                      openDropdown === item.name && 'rotate-180'
                    )} />
                  </button>
                  
                  {/* Dropdown Menu */}
                  {openDropdown === item.name && (
                    <div
                      className="absolute top-full right-0 mt-1 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-600 animate-in fade-in-0 zoom-in-95 duration-100"
                      style={{ zIndex: 9999 }}
                      data-dropdown-menu
                      onMouseEnter={() => setOpenDropdown(item.name)}
                      onMouseLeave={handleMouseLeave}
                    >
                      <div className="py-2">
                        {item.children.map((child) => (
                          <div key={child.name}>
                            {child.children ? (
                              // Submenu item with nested children
                              <div className="px-4 py-3">
                                <div className="flex items-center justify-between text-sm font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                                  <div className="flex items-center">
                                    <child.icon className="mr-3 h-4 w-4 flex-shrink-0" />
                                    {child.name}
                                    {child.badge && (
                                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300">
                                        {child.badge}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="mt-2 space-y-1">
                                  {child.children.map((subChild) => (
                                    <Link
                                      key={subChild.name}
                                      to={subChild.href}
                                      onClick={() => setOpenDropdown(null)}
                                      className={clsx(
                                        'flex items-center px-4 py-2 text-sm transition-colors hover:bg-gray-700 dark:hover:bg-gray-600 rounded-md',
                                        isActive(subChild.href)
                                          ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300'
                                          : 'text-gray-700 dark:text-gray-300 hover:text-white dark:hover:text-white'
                                      )}
                                    >
                                      <subChild.icon className="mr-3 h-4 w-4 flex-shrink-0" />
                                      {subChild.name}
                                    </Link>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              // Regular menu item
                              <Link
                                key={child.name}
                                to={child.href}
                                onMouseEnter={() => prefetchRouteData(child.href)}
                                onClick={() => setOpenDropdown(null)}
                                className={clsx(
                                  'flex items-center px-4 py-3 text-sm transition-colors hover:bg-gray-700 dark:hover:bg-gray-600',
                                  isActive(child.href)
                                    ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300'
                                    : 'text-gray-700 dark:text-gray-300 hover:text-white dark:hover:text-white'
                                )}
                              >
                                <child.icon className="mr-3 h-4 w-4 flex-shrink-0" />
                                {child.name}
                                {child.badge && (
                                  <span className="ml-auto inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300">
                                    {child.badge}
                                  </span>
                                )}
                              </Link>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to={item.href}
                  onMouseEnter={() => prefetchRouteData(item.href)}
                  className={clsx(
                    'flex items-center px-3 py-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
                    isActive(item.href)
                      ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  )}
                  title={item.description}
                >
                  <item.icon className="mr-2 h-4 w-4 flex-shrink-0" />
                  {item.name}
                </Link>
              )}
            </div>
          ))}
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <>
            {/* Backdrop - starts below header */}
            <div
              className="lg:hidden fixed left-0 right-0 top-[64px] bottom-0 bg-black/70 backdrop-blur-md z-40"
              onClick={() => setMobileMenuOpen(false)}
            />

            {/* Mobile Menu - Full screen overlay */}
            <div className="lg:hidden fixed left-0 right-0 top-[64px] bottom-0 bg-white dark:bg-gray-800 z-50 overflow-y-auto pb-4 space-y-1 px-4 pt-4">
              {dynamicNavigation.map((item) => (
                <div key={item.name}>
                  {item.children ? (
                    <div>
                      <button
                        onClick={() => handleDropdownToggle(item.name)}
                        className={clsx(
                          'w-full flex items-center justify-between px-3 py-2 text-sm font-medium rounded-md',
                          isParentActive(item)
                            ? 'bg-primary-100 text-primary-900 dark:bg-primary-900 dark:text-primary-100'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white'
                        )}
                      >
                        <div className="flex items-center">
                          <item.icon className="mr-3 h-5 w-5 flex-shrink-0" />
                          {item.name}
                          {item.badge && (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300">
                              {item.badge}
                            </span>
                          )}
                        </div>
                        <ChevronDown className={clsx(
                          'h-4 w-4 transition-transform',
                          openDropdown === item.name && 'rotate-180'
                        )} />
                      </button>
                      {openDropdown === item.name && (
                        <div className="mt-1 space-y-1 pl-8">
                          {item.children.map((child) => (
                            <div key={child.name}>
                              {child.children ? (
                                // Mobile submenu with nested children
                                <div className="space-y-1">
                                  <div className="flex items-center px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-400">
                                    <child.icon className="mr-3 h-4 w-4 flex-shrink-0" />
                                    {child.name}
                                    {child.badge && (
                                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300">
                                        {child.badge}
                                      </span>
                                    )}
                                  </div>
                                  <div className="pl-6 space-y-1">
                                    {child.children.map((subChild) => (
                                      <Link
                                        key={subChild.name}
                                        to={subChild.href}
                                        onClick={() => {
                                          setOpenDropdown(null)
                                          setMobileMenuOpen(false)
                                        }}
                                        className={clsx(
                                          'flex items-center px-3 py-2 text-sm rounded-md',
                                          isActive(subChild.href)
                                            ? 'bg-primary-100 text-primary-900 dark:bg-primary-900 dark:text-primary-100'
                                            : 'text-gray-600 hover:bg-gray-700 hover:text-white dark:text-gray-300 dark:hover:bg-gray-600 dark:hover:text-white'
                                        )}
                                      >
                                        <subChild.icon className="mr-3 h-4 w-4 flex-shrink-0" />
                                        {subChild.name}
                                      </Link>
                                    ))}
                                  </div>
                                </div>
                              ) : (
                                // Regular mobile menu item
                                <Link
                                  key={child.name}
                                  to={child.href}
                                  onClick={() => {
                                    setOpenDropdown(null)
                                    setMobileMenuOpen(false)
                                  }}
                                  className={clsx(
                                    'flex items-center px-3 py-2 text-sm rounded-md',
                                    isActive(child.href)
                                      ? 'bg-primary-100 text-primary-900 dark:bg-primary-900 dark:text-primary-100'
                                      : 'text-gray-600 hover:bg-gray-700 hover:text-white dark:text-gray-300 dark:hover:bg-gray-600 dark:hover:text-white'
                                  )}
                                >
                                  <child.icon className="mr-3 h-4 w-4 flex-shrink-0" />
                                  {child.name}
                                  {child.badge && (
                                    <span className="ml-auto inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300">
                                      {child.badge}
                                    </span>
                                  )}
                                </Link>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      to={item.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className={clsx(
                        'flex items-center px-3 py-2 text-sm font-medium rounded-md',
                        isActive(item.href)
                          ? 'bg-primary-100 text-primary-900 dark:bg-primary-900 dark:text-primary-100'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white'
                      )}
                    >
                      <item.icon className="mr-3 h-5 w-5 flex-shrink-0" />
                      {item.name}
                    </Link>
                  )}
                </div>
              ))}

              {/* Theme Switcher for Mobile */}
              <div className="pt-4 mt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between px-3 py-2">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-300">Theme</span>
                  <ThemeSwitcher />
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </nav>
  )
}